import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { SelectOption } from '@shared/components/inputs/SelectField/SelectField.types';
import type { ParsedAddOn } from '@t/addOn.types';
import { ADD_ON_CATEGORY_ALL, useConfigurationStore } from '@store/configurationStore';

export const useCategoryFilter = (channels: ParsedAddOn[]) => {
  const { category, setCategory } = useConfigurationStore(
    useShallow((s) => ({
      category: s.addOnFilterCategory,
      setCategory: s.setAddOnFilterCategory,
    }))
  );

  const categoryOptions: SelectOption[] = useMemo(() => {
    const names = new Set(channels.map((a) => a.categoryName).filter(Boolean) as string[]);
    return [
      ADD_ON_CATEGORY_ALL,
      ...Array.from(names)
        .sort()
        .map((name) => ({ label: name.charAt(0).toUpperCase() + name.slice(1), value: name })),
    ];
  }, [channels]);

  const categorySelectWidth = useMemo(() => {
    const longestLabel = categoryOptions.reduce((a, b) => (a.label.length > b.label.length ? a : b)).label;
    return longestLabel.length * 10 + 10;
  }, [categoryOptions]);

  const filteredChannels = useMemo(() => {
    const filtered =
      category.value === ADD_ON_CATEGORY_ALL.value
        ? channels
        : channels.filter((c) => c.categoryName === category.value);
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [channels, category]);

  const resetCategory = () => setCategory(ADD_ON_CATEGORY_ALL);

  return { category, setCategory, categoryOptions, categorySelectWidth, filteredChannels, resetCategory };
};
